import React, { useEffect } from 'react';
import { Box, Typography, Paper } from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import confetti from 'canvas-confetti';

export interface SuccessViewProps {
  message?: string;
  subMessage?: string;
  confettiConfig?: {
    particleCount?: number;
    spread?: number;
    colors?: string[];
    origin?: { x?: number; y?: number };
  };
  showIcon?: boolean;
  variant?: 'standard' | 'celebration' | 'minimal';
  onComplete?: () => void;
  autoRedirectDelay?: number;
}

const SuccessView: React.FC<SuccessViewProps> = ({
  message = "Success!",
  subMessage = "Your request has been processed successfully.",
  confettiConfig = {},
  showIcon = true,
  variant = 'standard',
  onComplete,
  autoRedirectDelay = 0
}) => {
  useEffect(() => {
    // Default confetti configuration
    const defaultConfig = {
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      colors: ['#4caf50', '#81c784', '#a5d6a7', '#c8e6c9']
    };

    // Merge with custom config
    const finalConfig = { ...defaultConfig, ...confettiConfig };

    // Trigger confetti based on variant
    const triggerConfetti = () => {
      switch (variant) {
        case 'celebration':
          // Multiple bursts for celebration
          const count = 200;
          const defaults = { origin: { y: 0.7 } };
          
          function fire(particleRatio: number, opts: any) {
            confetti({
              ...defaults,
              ...opts,
              particleCount: Math.floor(count * particleRatio),
              colors: finalConfig.colors
            });
          }
          
          fire(0.25, { spread: 26, startVelocity: 55 });
          fire(0.2, { spread: 60 });
          fire(0.35, { spread: 100, decay: 0.91, scalar: 0.8 });
          fire(0.1, { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2 });
          fire(0.1, { spread: 120, startVelocity: 45 });
          break;
        
        case 'minimal':
          // Subtle confetti for minimal variant
          confetti({
            ...finalConfig,
            particleCount: 50,
            spread: 45,
            startVelocity: 25
          });
          break;
        
        default:
          // Standard confetti
          confetti(finalConfig);
      }
    };

    // Trigger confetti with a slight delay for better visual effect
    const confettiTimer = setTimeout(triggerConfetti, 100);

    // Auto-redirect if delay is specified
    let redirectTimer: NodeJS.Timeout | undefined;
    if (autoRedirectDelay > 0 && onComplete) {
      redirectTimer = setTimeout(onComplete, autoRedirectDelay);
    }

    return () => {
      clearTimeout(confettiTimer);
      if (redirectTimer) {
        clearTimeout(redirectTimer);
      }
    };
  }, [confettiConfig, variant, autoRedirectDelay, onComplete]);

  const getIconSize = () => {
    switch (variant) {
      case 'minimal':
        return 48;
      case 'celebration':
        return 120;
      default:
        return 80;
    }
  };

  const getMessageSize = () => {
    switch (variant) {
      case 'minimal':
        return 'h5';
      case 'celebration':
        return 'h3';
      default:
        return 'h4';
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      <Paper
        elevation={variant === 'minimal' ? 1 : 3}
        sx={{
          p: variant === 'minimal' ? 3 : 4,
          maxWidth: variant === 'celebration' ? 600 : 500,
          textAlign: 'center',
          borderRadius: variant === 'celebration' ? 3 : 2,
        }}
      >
        {showIcon && (
          <Box sx={{ mb: 3 }}>
            <CheckCircle
              sx={{
                fontSize: getIconSize(),
                color: 'success.main',
                mb: 2,
                animation: variant === 'celebration' ? 'bounce 1s infinite' : 'pulse 2s infinite',
                '@keyframes bounce': {
                  '0%, 20%, 53%, 80%, 100%': {
                    transform: 'translate3d(0,0,0)',
                  },
                  '40%, 43%': {
                    transform: 'translate3d(0, -30px, 0)',
                  },
                  '70%': {
                    transform: 'translate3d(0, -15px, 0)',
                  },
                  '90%': {
                    transform: 'translate3d(0, -4px, 0)',
                  },
                },
                '@keyframes pulse': {
                  '0%': {
                    transform: 'scale(1)',
                  },
                  '50%': {
                    transform: 'scale(1.1)',
                  },
                  '100%': {
                    transform: 'scale(1)',
                  },
                },
              }}
            />
          </Box>
        )}

        <Typography 
          variant={getMessageSize() as any} 
          gutterBottom 
          color="success.main"
          sx={{ 
            fontWeight: 'bold',
            mb: variant === 'celebration' ? 3 : 2
          }}
        >
          {message}
        </Typography>

        <Typography 
          variant="body1" 
          sx={{ 
            mb: variant === 'minimal' ? 2 : 3, 
            color: 'text.secondary',
            fontSize: variant === 'celebration' ? '1.1rem' : '1rem'
          }}
        >
          {subMessage}
        </Typography>

        {autoRedirectDelay > 0 && (
          <Typography 
            variant="caption" 
            sx={{ 
              color: 'text.disabled',
              fontStyle: 'italic',
              animation: 'pulse 2s infinite'
            }}
          >
            Redirecting in a moment...
          </Typography>
        )}
      </Paper>
    </Box>
  );
};

export default SuccessView;

// Success variations for easy use
export const successVariations = {
  subscription: {
    message: "Subscription Successful!",
    subMessage: "Welcome! You now have full access to all features.",
    variant: 'celebration' as const,
    confettiConfig: { 
      particleCount: 150, 
      spread: 90,
      colors: ['#4caf50', '#8bc34a', '#cddc39', '#ffeb3b']
    }
  },
  payment: {
    message: "Payment Processed!",
    subMessage: "Your payment has been successfully processed.",
    variant: 'standard' as const,
    confettiConfig: { 
      particleCount: 100, 
      spread: 70,
      colors: ['#4caf50', '#81c784']
    }
  },
  saved: {
    message: "Changes Saved!",
    subMessage: "Your changes have been saved successfully.",
    variant: 'minimal' as const,
    confettiConfig: { 
      particleCount: 50, 
      spread: 45
    }
  },
  approved: {
    message: "Access Approved!",
    subMessage: "You have been granted access to the application.",
    variant: 'celebration' as const,
    confettiConfig: { 
      particleCount: 200, 
      spread: 100,
      colors: ['#2196f3', '#03a9f4', '#00bcd4', '#009688']
    }
  },
  uploaded: {
    message: "Upload Complete!",
    subMessage: "Your files have been uploaded successfully.",
    variant: 'standard' as const,
    confettiConfig: { 
      particleCount: 75, 
      spread: 60,
      colors: ['#ff9800', '#ffc107', '#ffeb3b']
    }
  },
  deleted: {
    message: "Successfully Deleted!",
    subMessage: "The item has been removed from the system.",
    variant: 'minimal' as const,
    confettiConfig: { 
      particleCount: 30, 
      spread: 40,
      colors: ['#f44336', '#e57373']
    }
  }
};